import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  CalendarToday as CalendarIcon,
  Settings as SettingsIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

export default function ConstraintsView() {
  const { user, isAuthenticated, isAdmin } = useAuth();
  const { language, t } = useLanguage();
  const [constraints, setConstraints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedConstraint, setSelectedConstraint] = useState(null);
  const [dateStart, setDateStart] = useState(null);
  const [dateEnd, setDateEnd] = useState(null);
  
  // Week-based schedule generation state
  const [selectedWeekStart, setSelectedWeekStart] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generateSuccess, setGenerateSuccess] = useState(null);
  const [generateError, setGenerateError] = useState(null);
  
  // Helper: Get next Monday from a date (or same date if already Monday)
  const getNextMonday = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = day === 0 ? 1 : day === 1 ? 0 : 8 - day;
    d.setDate(d.getDate() + diff);
    return d;
  };
  
  // Helper: Format week range for display
  const formatWeekRange = (mondayDate) => {
    if (!mondayDate) return '';
    const monday = new Date(mondayDate);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    
    const options = { month: 'short', day: 'numeric' };
    const mondayStr = monday.toLocaleDateString('en-US', options);
    const sundayStr = sunday.toLocaleDateString('en-US', { ...options, year: 'numeric' });
    
    return `Week of ${mondayStr} - ${sundayStr}`;
  };
  
  // Initialize selectedWeekStart to next Monday on mount
  useEffect(() => {
    setSelectedWeekStart(getNextMonday(new Date()));
  }, []);

  useEffect(() => {
    loadConstraints();
  }, []);

  const loadConstraints = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get('/api/constraints');
      if (data.success) {
        setConstraints(data.constraints);
      } else {
        setError(data.message || t('constraintsView.failedToLoad'));
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message || t('constraintsView.failedToLoad'));
    }
    setLoading(false);
  };

  const handleEditDates = (constraint) => {
    setSelectedConstraint(constraint);
    setDateStart(constraint.date_start ? new Date(constraint.date_start) : null);
    setDateEnd(constraint.date_end ? new Date(constraint.date_end) : null);
    setEditDialogOpen(true);
  };

  const handleSaveDates = async () => {
    if (!selectedConstraint) return;

    setLoading(true);
    try {
      const { data } = await axios.put(`/api/constraints/${selectedConstraint.id}/dates`, {
        date_start: dateStart ? dateStart.toISOString().split('T')[0] : null,
        date_end: dateEnd ? dateEnd.toISOString().split('T')[0] : null
      });

      if (data.success) {
        setEditDialogOpen(false);
        loadConstraints(); // Refresh the list
      } else {
        setError(data.message || t('constraintsView.failedToUpdate'));
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message || t('constraintsView.failedToUpdate'));
    }
    setLoading(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('common.notSet');
    return new Date(dateString).toLocaleDateString();
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Business': '#4CAF50',
      'Employee': '#2196F3',
      'Shift': '#FF9800',
      'Availability': '#9C27B0',
      'Other': '#757575'
    };
    return colors[category] || '#757575';
  };
  
  const handleGenerateSchedule = async () => {
    if (!selectedWeekStart) {
      setGenerateError(t('constraintsView.pleaseSelectWeek'));
      return;
    }
    
    // Ensure it's a Monday
    const weekDate = new Date(selectedWeekStart);
    if (weekDate.getDay() !== 1) {
      setSelectedWeekStart(getNextMonday(weekDate));
      return;
    }
    
    setGenerating(true);
    setGenerateError(null);
    setGenerateSuccess(null);
    
    try {
      const weekStartStr = weekDate.toISOString().split('T')[0];
      const { data } = await axios.post('/api/schedule/generate-for-week', {
        week_start_date: weekStartStr,
        language
      });
      
      if (data.success) {
        setGenerateSuccess(t('constraintsView.scheduleGenerated', { weekRange: formatWeekRange(weekDate) }));
      } else {
        setGenerateError(data.message || t('constraintsView.failedToGenerate'));
      }
    } catch (e) {
      setGenerateError(e.response?.data?.detail || e.message || t('constraintsView.failedToGenerate'));
    }
    
    setGenerating(false);
  };

  const renderConstraintsTable = () => {
    if (!constraints || constraints.length === 0) {
      return (
        <Alert severity="info">
          {t('constraintsView.noConstraintsFound')}
        </Alert>
      );
    }

    return (
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('constraintsView.name')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('constraintsView.category')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('constraintsView.description')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('constraintsView.weight')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('constraintsView.dateStart')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('constraintsView.dateEnd')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('constraintsView.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {constraints.map((constraint) => (
              <TableRow key={constraint.id}>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  {constraint.name}
                </TableCell>
                <TableCell>
                  <Chip
                    label={constraint.category || t('common.uncategorized')}
                    size="small"
                    sx={{
                      backgroundColor: getCategoryColor(constraint.category),
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ maxWidth: 300 }}>
                    {constraint.description || t('common.noDescription')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={constraint.weight || '1.0'}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {formatDate(constraint.date_start)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {formatDate(constraint.date_end)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                    <Tooltip title={t('constraintsView.editDates')}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditDates(constraint)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderSummary = () => {
    const totalConstraints = constraints.length;
    const withDates = constraints.filter(c => c.date_start && c.date_end).length;
    const withoutDates = totalConstraints - withDates;

    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SettingsIcon color="primary" />
                <Typography variant="h6">{totalConstraints}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t('constraintsView.totalConstraints')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon color="success" />
                <Typography variant="h6">{withDates}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t('constraintsView.withDateRange')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon color="warning" />
                <Typography variant="h6">{withoutDates}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t('constraintsView.withoutDates')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" color={withDates > 0 ? 'success.main' : 'warning.main'}>
                  {totalConstraints > 0 ? Math.round((withDates / totalConstraints) * 100) : 0}%
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t('constraintsView.dateCoverage')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  // Check authentication
  if (!isAuthenticated) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{t('constraintsView.pleaseLogin')}</Alert>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              {t('constraintsView.title')}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {t('constraintsView.subtitle')}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadConstraints}
            disabled={loading}
          >
            {t('common.refresh')}
          </Button>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <>
            {renderSummary()}
            
            {/* Week-based Schedule Generation */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('constraintsView.generateScheduleForWeek')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('constraintsView.generateScheduleDescription')}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <Box sx={{ minWidth: 250 }}>
                  <DatePicker
                    label={t('constraintsView.weekStartDate')}
                    value={selectedWeekStart}
                    onChange={(newValue) => {
                      if (newValue) {
                        setSelectedWeekStart(getNextMonday(newValue));
                      }
                    }}
                    shouldDisableDate={(date) => {
                      return date.getDay() !== 1; // Only allow Mondays
                    }}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                  {selectedWeekStart && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {formatWeekRange(selectedWeekStart)}
                    </Typography>
                  )}
                </Box>
                
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleGenerateSchedule}
                  disabled={generating || !selectedWeekStart}
                  startIcon={generating ? <CircularProgress size={20} /> : <CalendarIcon />}
                  sx={{ mt: 1 }}
                >
                  {generating ? t('constraintsView.generating') : t('constraintsView.generateSchedule')}
                </Button>
              </Box>
              
              {generateSuccess && (
                <Alert severity="success" sx={{ mt: 2 }} onClose={() => setGenerateSuccess(null)}>
                  {generateSuccess}
                </Alert>
              )}
              
              {generateError && (
                <Alert severity="error" sx={{ mt: 2 }} onClose={() => setGenerateError(null)}>
                  {generateError}
                </Alert>
              )}
            </Paper>
            
            {renderConstraintsTable()}
          </>
        )}

        {/* Edit Dates Dialog */}
        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {t('constraintsView.editDateRange')} "{selectedConstraint?.name}"
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <DatePicker
                label={t('constraintsView.startDate')}
                value={dateStart}
                onChange={setDateStart}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
              <DatePicker
                label={t('constraintsView.endDate')}
                value={dateEnd}
                onChange={setDateEnd}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSaveDates} variant="contained" disabled={loading}>
              {t('constraintsView.saveDates')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
}
